import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

interface Weight {
  modifierWeightId: string;
  name: string;
  modifierWeightCode?: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  weights?: Weight[];
  [key: string]: any;
}

interface WeightSelectProps {
  value: Weight | null | Weight[];
  onChange: (weight: Weight | null | Weight[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  multiple?: boolean;
}

export function WeightSelect({
  value,
  onChange,
  product,
  menu,
  multiple = true,
}: WeightSelectProps) {
  const [weights, setWeights] = useState<Weight[]>([]);
  const [weightsLoading, setWeightsLoading] = useState(false);

  useEffect(() => {
    setWeightsLoading(true);
    
    // If no product is provided, try to get all weights from the menu
    if (!product && menu && menu.weights && Array.isArray(menu.weights)) {
      console.log('WeightSelect - Using all weights from menu:', menu.weights.length, 'weights');
      setWeights(menu.weights);
      setWeightsLoading(false);
      return;
    }
    
    // Extract weights from product's variants
    if (product && product.variants && Array.isArray(product.variants)) {
      // Use the first variant by default, or check if there's a selected variant in product
      const selectedVariant = product.selectedVariant || product.variants[0];
      
      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        // Extract weights from all modifiers in all slots
        const allWeights: Weight[] = [];
        
        selectedVariant.slots.forEach((slotItem: any) => {
          if (slotItem.modifiers && Array.isArray(slotItem.modifiers)) {
            slotItem.modifiers.forEach((modifierItem: any) => {
              if (modifierItem.weights && Array.isArray(modifierItem.weights)) {
                modifierItem.weights.forEach((weightItem: any) => {
                  if (weightItem.weight) {
                    allWeights.push(weightItem.weight);
                  }
                });
              }
            });
          }
        });
        
        // Remove duplicates by modifierWeightId
        const uniqueWeights = allWeights.filter((weight: Weight, index: number, self: Weight[]) => 
          index === self.findIndex(w => w.modifierWeightId === weight.modifierWeightId)
        );
        
        console.log('WeightSelect - Extracted from product.variants[].slots[].modifiers[].weights:', 
                   uniqueWeights.length, 'weights', uniqueWeights.map((w: any) => w.name));
        setWeights(uniqueWeights);
      } else {
        console.log('WeightSelect - No slots found in selected variant:', selectedVariant);
        setWeights([]);
      }
    } else {
      console.log('WeightSelect - No variants available in product:', product);
      setWeights([]);
    }
    
    setWeightsLoading(false);
  }, [product, menu]);

  // Make sure our controlled `value` never defaults to the *entire* list
  // If multiple: value must be an array; otherwise null
  const safeValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;


  const getLabel = (option: Weight) => {
    if (!option) return 'No weight';
    if (!option.name && !option.modifierWeightCode) return 'Unknown';
    return `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={Array.isArray(weights) ? weights : []}
      disableCloseOnSelect
      filterSelectedOptions
      value={safeValue}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.modifierWeightId === (val as Weight).modifierWeightId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.modifierWeightCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.modifierWeightId || `weight-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!multiple || !Array.isArray(tagValue)) return [];
        return tagValue.map((option, idx) => {
          if (!option) return null;
          const label = (!option.name && !option.modifierWeightCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index: idx })}
              key={option.modifierWeightId}
              size="small"
              label={label}
            />
          );
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Weight"
          placeholder="Search for weights..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {weightsLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
